import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { CountryISO } from 'ngx-intl-tel-input';
import { finalize, Observable, ReplaySubject, Subject } from 'rxjs';
import { CatalogService } from './data/catalog.service';
import { FormValuesService } from './form-values.service';
import { SpinnerService } from './spinner.service';

export interface onChangesHook {
  (self: { context: any; field: FormlyFieldConfig }, value: any, model?: any, selectedOption?: any): void;
}

@Injectable({
  providedIn: 'root',
})
export class FormHooksService {
  private fields: FormlyFieldConfig[] = [];
  private options: FormlyFormOptions = { formState: {} };
  private selectedCountry: ReplaySubject<{ data: CountryISO; emitter: string }> = new ReplaySubject<{
    data: CountryISO;
    emitter: string;
  }>(1);
  public selectedCountry$ = this.selectedCountry.asObservable();
  private samePrimaryPhone: Subject<{ value: any }> = new Subject<{ value: any }>();
  public samePrimaryPhone$ = this.samePrimaryPhone.asObservable();

  constructor(
    private spinnerService: SpinnerService,
    private catalogService: CatalogService,
    private formValuesService: FormValuesService
  ) {}

  public init(config: {
    fieldsConfig: FormlyFieldConfig[];
    formlyOptions: FormlyFormOptions;
    hooks: { fieldKey: string; onChanges: (self: { context: any; field: FormlyFieldConfig }, value: any) => void }[];
    whenDone: (fields: FormlyFieldConfig[]) => void;
  }): void {
    this.fields = [...config.fieldsConfig];
    this.options = config.formlyOptions;
    // Do not reuse the previous form's country when a new form has no preference.
    this.selectedCountry.next({ data: CountryISO.UnitedStates, emitter: 'inquirerCountry' });

    config.hooks.forEach((hook) => {
      this.fields.forEach((group) => {
        if (group.fieldGroup) {
          group.fieldGroup.forEach((field) => {
            if (field.templateOptions && field.key === hook.fieldKey) {
              field.templateOptions['onChange'] = hook.onChanges;
            }
          });
        }
      });
    });

    config.whenDone(this.fields);
  }

  public subcategoryHook(conf: { target: string }): onChangesHook {
    return (self: { context: any; field: FormlyFieldConfig }, value: any): void => {
      if (value) {
        this.hook({
          ...conf,
          request: this.catalogService.subcategoryByCategory(value),
          callback: (response, target) => {
            this.options.formState[target] = response;
          },
        });
      }
      this.formValuesService.update({
        key: 'caseSubCategory',
        fieldsConfig: this.fields,
        withValues: [],
      });
    };
  }

  public shipmentIdentifierHook(conf: { target: string }): onChangesHook {
    return (self: { context: any; field: FormlyFieldConfig }, value: any): void => {
      if (value) {
        const mandatoryShipmentIdentifier: boolean = this.options.formState['caseSubCategory'].find(
          (subCategory: any) => subCategory.id === value
        ).mandatoryShipmentIdentifier;

        this.options.formState['shipmentIdentifierValues'] = {
          required: mandatoryShipmentIdentifier,
        };
      }
    };
  }

  public businessUnitHook(conf: {
    target: string;
    stateTarget: string;
    defaultValue?: any;
    model?: any;
    stateDefault?: any;
  }): onChangesHook {
    return (self: { context: any; field: FormlyFieldConfig }, value: any): void => {
      const modelRef = { ...conf.model };
      this.formValuesService.update({
        key: conf.target as string,
        fieldsConfig: this.fields,
        withValues: [],
      });
      if (value) {
        if (self.field.key) {
          this.selectedCountry.next({ data: value.toLowerCase(), emitter: self.field.key as string });
        }
        this.hook({
          ...conf,
          default: conf.defaultValue,
          request: this.catalogService.businessUnitByCountry(value),
          callback: (response: any[]) => {
            const preselectedBusinessUnit = response.find(
              (businessUnit: any) => businessUnit.apiName === conf.model['businessUnit']
            );
            this.fields.forEach((fieldSection) => {
              if (fieldSection.fieldGroup) {
                fieldSection.fieldGroup.find((field) => {
                  if (field.key === 'countryAssistance') {
                    field.formControl?.patchValue(conf.model['inquirerCountry']);
                  }
                });
              }
            });
            this.formValuesService.update({
              key: 'caseCategory',
              fieldsConfig: this.fields,
              withValues: [],
            });
            if (preselectedBusinessUnit) {
              this.hook({
                target: 'caseCategory',
                request: this.catalogService.categoryByBusinessUnit(preselectedBusinessUnit.id),
                default: modelRef['caseCategory'],
                callback: (categories) => {
                  const preselectedCategory = categories.find(
                    (category: any) => category.apiName === modelRef['caseCategory']
                  );
                  if (preselectedCategory) {
                    this.hook({
                      target: 'caseSubCategory',
                      request: this.catalogService.subcategoryByCategory(preselectedCategory.id),
                      default: modelRef['caseSubCategory'],
                      callback: (reponse) => {
                        const responseRef = [...reponse];
                        this.options.formState['caseSubCategory'] = [...responseRef];

                        const mandatoryShipmentIdentifier: boolean = responseRef.find(
                          (subCategory: any) => subCategory.name === modelRef['caseSubCategory']
                        ).mandatoryShipmentIdentifier;

                        this.options.formState['caseSubCategory'] = [...responseRef];

                        this.options.formState['shipmentIdentifierValues'] = {
                          required: mandatoryShipmentIdentifier,
                        };
                      },
                    });
                  }
                },
              });
            }
          },
        });
        this.hook({
          target: conf.stateTarget,
          default: conf.stateDefault,
          request: this.catalogService.stateProvinceByCountry(value),
          callback: this.updateFormStateForStateProvince,
        });
      }
      this.formValuesService.update({
        key: conf.target as string,
        fieldsConfig: this.fields,
        withValues: [],
      });
      this.formValuesService.update({
        key: 'caseCategory',
        fieldsConfig: this.fields,
        withValues: [],
      });
      this.formValuesService.update({
        key: 'caseSubCategory',
        fieldsConfig: this.fields,
        withValues: [],
      });
    };
  }

  public categoryHook(conf: { target: string; defaultValue?: any }): onChangesHook {
    return (self: { context: any; field: FormlyFieldConfig }, value: any): void => {
      if (value) {
        this.hook({ ...conf, default: conf.defaultValue, request: this.catalogService.categoryByBusinessUnit(value) });
      }

      this.formValuesService.update({
        key: 'caseCategory',
        fieldsConfig: this.fields,
        withValues: [],
      });

      this.formValuesService.update({
        key: 'caseSubCategory',
        fieldsConfig: this.fields,
        withValues: [],
      });
    };
  }

  public copyIfSameAsCallerHook(conf: { target: string; form: FormGroup }): onChangesHook {
    return (self: { context: any; field: FormlyFieldConfig }, _value: any, model: any, selectedOption: any): void => {
      if (model?.sameAsCaller) {
        conf.form.patchValue({ [conf.target]: selectedOption.value });
      }
    };
  }

  public resetpremierShipmentHook(conf: { target: string; form: FormGroup }): onChangesHook {
    return (self: { context: any; field: FormlyFieldConfig }, _value: any, model: any, selectedOption: any): void => {
       // if premierShipment toggle  is off  then only clear text box value 
       conf.form.patchValue({ ["premierShipment"]: '' });
       
       //patch 'Premier Shipment' in caseInstructions text area in red color
       if(conf?.form?.value?.premierShipmentNotes){
        conf.form.patchValue({ ["caseInstructions"]: 'Premier Shipment' });
        var x = document.getElementsByClassName("slds-textarea")[1];
        x.setAttribute("style","color:red"); 
       }else{
        conf.form.patchValue({ ["caseInstructions"]: '' }); 
        var x = document.getElementsByClassName("slds-textarea")[1];        
        x.setAttribute("style","color:black");
       }
    };
  }

  public stateProvinceHook(conf: { target: string; defaultValue?: any; model?: any }): onChangesHook {
    return (self: { context: any; field: FormlyFieldConfig }, value: any): void => {
      this.hook({
        ...conf,
        default: conf.defaultValue,
        request: this.catalogService.stateProvinceByCountry(value),
        callback: (response: any[], target: string) => {
          this.updateFormStateForStateProvince(response, target);
          this.selectedCountry.next({ data: value.toLowerCase(), emitter: self.field.key as string });
        },
      });
    };
  }

  public isPrimaryHook(): onChangesHook {
    return (self: { context: any; field: FormlyFieldConfig }, value: any, model?: any): void => {
      let newVal = null;

      if (value) {
        newVal = model['primaryPhone'];
      }

      model['mobilePhone'] = newVal;
      this.formValuesService.updateInput({
        key: 'mobilePhone',
        fieldsConfig: this.fields,
        values: newVal,
        whenDone: (updatedConfig) => {
          this.fields = updatedConfig;
        },
      });
    };
  }

  public sameAsCallerHook(config: { whenDone: (fields: FormlyFieldConfig[], values: any) => void }): onChangesHook {
    const assistanceFields: any = {
      firstNameAssistance: { newKey: 'firstName', mirrored: false },
      lastNameAssistance: { newKey: 'lastName', mirrored: false },
      accountNumberAssistance: { newKey: 'accountNumber', mirrored: false },
      countryAssistance: { newKey: 'country', mirrored: true },
      streetAssistance: { newKey: 'street', mirrored: true },
      cityAssistance: { newKey: 'city', mirrored: true },
      stateAssistance: { newKey: 'state', mirrored: true },
      postalCodeAssistance: { newKey: 'postalCode', mirrored: true },
      phoneExtAssistance: { newKey: 'phoneExt', mirrored: false },
      emailAssistance: { newKey: 'email', mirrored: false },
      contactMethodAssistance: { newKey: 'contactMethod', mirrored: false },
      contactLanguageAssistance: { newKey: 'contactLanguage', mirrored: false },
    };

    function getOriginalKey(field: FormlyFieldConfig): string {
      return (field?.templateOptions && field.templateOptions['originalKey']) || (field.key as string);
    }
    function initOriginalKey(field: FormlyFieldConfig): void {
      if (!field.templateOptions) {
        field.templateOptions = {};
      }
      if (!field.templateOptions['originalKey']) {
        field.templateOptions['originalKey'] = field.key as string;
      }
    }

    const updateAssistanceField = (
      field: FormlyFieldConfig,
      sameAsCaller: boolean,
      model: any,
      values: any
    ): FormlyFieldConfig => {
      // Skip if we don't even have a key
      if (!field.key) return field;

      const originalKey = getOriginalKey(field);
      const fieldConfig = assistanceFields[originalKey];
      if (fieldConfig) {
        initOriginalKey(field);

        const newValue = model[fieldConfig.newKey];

        if (fieldConfig.mirrored) {
          field.expressionProperties ||= {};
          if (!field.expressionProperties['templateOptions.disabled']) {
            field.expressionProperties['templateOptions.disabled'] = (model) => model.sameAsCaller;
          }
          if (newValue) {
            values[originalKey] = newValue;
          }
          return { ...field };
        } else if (newValue && !model[originalKey]) {
          values[originalKey] = newValue;
        }
      }
      return field;
    };

    return (_self: { context: any; field: FormlyFieldConfig }, sameAsCaller: boolean, model: any): void => {
      this.samePrimaryPhone.next({ value: model['primaryPhone'] });
      const values = {};
      this.fields = this.fields.map((fg) => {
        if (fg.fieldGroup) {
          let fieldsWereUpdated = false;
          const newFields = fg.fieldGroup.map((field) => {
            const newField = updateAssistanceField(field, sameAsCaller, model, values);
            if (field !== newField) {
              fieldsWereUpdated = true;
            }
            return newField;
          });
          if (fieldsWereUpdated) {
            // Update fieldGroup if fields were updated
            fg.fieldGroup = newFields;
          }
        }
        return fg;
      });
      config.whenDone(this.fields, values);
    };
  }

  private updateFormStateForStateProvince = (response: any, key: string) => {
    const hasStates = response.length > 0;
    const formState = this.options.formState;
    if (!formState[key]) {
      formState[key] = {};
    }
    if (!formState[key].hasStates || formState[key].hasStates !== hasStates) {
      formState[key].hasStates = hasStates;
    }
  };

  private hook(conf: {
    target: string;
    default?: any;
    request: Observable<any[]>;
    hideWhen?: (response: any) => (model: any, formState: any, field?: FormlyFieldConfig | undefined) => boolean;
    callback?: (response: any, target: string) => void;
  }): void {
    this.spinnerService.show();
    conf.request.pipe(finalize(() => this.spinnerService.hide())).subscribe((response: any[]) => {
      const hideWhen = typeof conf.hideWhen === 'function' ? conf.hideWhen(response) : undefined;
      this.formValuesService.update({
        key: conf.target as string,
        fieldsConfig: this.fields,
        withValues: response,
        defaultValue: conf.default,
        hideWhen,
        whenDone: (updatedConfig) => {
          this.fields = updatedConfig;
          if (conf.callback) {
            conf.callback(response, conf.target);
          }
        },
      });
    });
  }
}
